import type { DataLayerInput } from '@corratech/pylot-tag-manager'
import {
    deepValue,
    isItemWithKeys,
    isNonNullRecord
} from '@corratech/pylot-utils'
import {
    GTMEventDataMapping,
    MappingItem,
    GenericMappingFunction,
    MappingItemWithSource
} from '../GTM.d'

const isMappingItemWithSource = (
    item: MappingItem
): item is MappingItemWithSource => {
    return !Array.isArray(item) && Object.keys(item).includes('data_source')
}

const isMultiPathMap = (item: unknown): item is string[][] => {
    if (!item || !Array.isArray(item) || item.length === 0) {
        return false
    }
    let result = true
    item.forEach((subItem: unknown) => {
        if (!Array.isArray(subItem)) {
            result = false
        } else {
            subItem.forEach((subSubItem: unknown) => {
                if (typeof subSubItem !== 'string') {
                    result = false
                }
            })
        }
    })
    return result
}

const isCallableMapping = (item: unknown): item is GenericMappingFunction => {
    return typeof item === 'function'
}

//NOSONAR
const resolveFieldValue = (
    data: Record<string, unknown>,
    fieldMap: MappingItem,
    key?: string
) => {
    let fieldValue
    const defaultValue = key ? data[key] : ''
    switch (typeof fieldMap) {
        // Sub-object with custom data source (from another field)
        // See productsDetails array, built from cart items
        case 'object':
            if (isMappingItemWithSource(fieldMap)) {
                const sourceData = resolveFieldValue(
                    data,
                    fieldMap['data_source']
                )
                if (sourceData) {
                    fieldValue = applyMapping(
                        sourceData,
                        fieldMap['mapping'],
                        fieldMap['strict']
                    )
                }
                break
            }

            // Simple mapped sub-object
            if (
                !Array.isArray(fieldMap) &&
                key &&
                Object.keys(data).includes(key)
            ) {
                const subObject = data[key]
                if (isItemWithKeys(subObject)) {
                    fieldValue = applyMapping(subObject, fieldMap)
                    break
                }
            }

            // Multiple field paths in an object - get the first defined value
            if (isMultiPathMap(fieldMap)) {
                let finalValue: unknown
                fieldMap.forEach((path) => {
                    const currentValue = deepValue(data, path, undefined)
                    if (
                        typeof finalValue === 'undefined' &&
                        typeof currentValue !== 'undefined'
                    ) {
                        finalValue = currentValue
                    }
                })
                fieldValue = finalValue || defaultValue
                break
            }

            // Simple field path in an object
            if (Array.isArray(fieldMap)) {
                fieldValue = deepValue(data, fieldMap, defaultValue)
                break
            }

            fieldValue = defaultValue
            break

        // Custom resolver function
        // eslint-disable-next-line no-fallthrough
        case 'function':
            if (isCallableMapping(fieldMap)) {
                fieldValue = fieldMap(data)
                break
            }

            fieldValue = defaultValue
            break

        default:
            fieldValue = defaultValue
    }

    return fieldValue
}
//SONAR

const mapSingleObject = (
    data: Record<string, unknown>,
    mapping: MappingItem,
    strict = false
) => {
    const mappedData: typeof data = {}
    Object.entries(mapping).forEach(([key, fieldMap]) => {
        mappedData[key] = resolveFieldValue(data, fieldMap, key)
    })

    return strict ? mappedData : { ...data, ...mappedData }
}

// Process data input (object|array), where field values need to be resolved from input
// strict - if true, return only mapped fields, otherwise - replace mapped fields in the initial data
const applyMapping = (
    data: Record<string, unknown> | unknown[],
    mapping: MappingItem,
    strict = false
) => {
    if (!Array.isArray(data)) {
        return mapSingleObject(data, mapping, strict)
    }

    const mappedData: unknown[] = []
    data.forEach((item) => {
        if (isNonNullRecord(item)) {
            mappedData.push(mapSingleObject(item, mapping, strict))
        }
    })
    return mappedData
}

// Apply mapping to the data input, if possible
export const applyDataMapping = (
    action: DataLayerInput,
    mapping: GTMEventDataMapping
): DataLayerInput => {
    if (typeof mapping[action.type] === 'undefined' || !mapping[action.type]) {
        return action
    }

    const mappedAction = { ...action }
    try {
        mappedAction.data = applyMapping(action.data, mapping[action.type])
    } catch (e) {
        console.log(e)
        return action
    }

    return mappedAction
}
